import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ComicCard from "@/components/feature/ComicCard";
import { comics, todayUpdates, categories, faqItems, getComicById } from "@/mocks/comics";

const ongoing = comics.filter((c) => c.status === "连载中");
const completed = comics.filter((c) => c.status === "已完结");
const trending = comics.filter((c) => c.weekRank).sort((a, b) => (a.weekRank || 0) - (b.weekRank || 0));

function SectionHeader({ id, title, desc, link }: { id?: string; title: string; desc?: string; link?: { text: string; to: string } }) {
  return (
    <div id={id} className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
      <div>
        <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h2>
        {desc && <p className="mt-1 text-sm text-foreground-600">{desc}</p>}
      </div>
      {link && (
        <Link
          to={link.to}
          className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          查看全部 <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const [keyword, setKeyword] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(keyword.trim() ? `/list?search=${encodeURIComponent(keyword.trim())}` : "/list");
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "无尽漫画网首页",
            description: "无尽漫画网今日更新、最新话、连载状态与追更进度查询。",
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "无尽漫画网热门追更榜",
            itemListElement: trending.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: c.title,
              url: `https://comicstatus.github.io/comic/${c.id}`,
            })),
          }),
        }}
      />

      {/* 首屏 */}
      <section className="relative flex w-full items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20manga%20comic%20cityscape%20at%20sunrise%2C%20coral%20orange%20and%20gold%20sky%20over%20rooftops%20with%20giant%20dynamic%20ink%20brush%20strokes%2C%20artistic%20illustrated%20background%20with%20floating%20comic%20panels%20and%20speed%20lines%2C%20warm%20vibrant%20energy%2C%20cinematic%20wide%20composition&width=1600&height=900&seq=home-hero-01&orientation=landscape"
          alt="无尽漫画网 漫画追更资讯门户首屏插画"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/55"></div>
        <div className="relative z-10 w-full max-w-4xl px-4 py-20 text-center md:py-28">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50 backdrop-blur-sm">
            <i className="ri-flashlight-fill text-accent-300"></i>
            今日更新 · 最新话 · 追更进度，一站掌握
          </p>
          <h1 className="font-display text-4xl leading-tight text-background-50 md:text-6xl">
            无尽漫画网
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-background-100 md:text-lg">
            想知道今天更了什么、你追的漫画更新到第几话？按连载状态筛选，完结与连载中分开排列，追更进度一目了然。
          </p>
          <form onSubmit={handleSearch} className="mx-auto mt-8 flex w-full max-w-xl items-center rounded-full border border-background-50/30 bg-background-50/95 p-1.5 shadow-sm">
            <i className="ri-search-line pl-3 text-base text-secondary-500"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入漫画名，查询最新话 / 追更进度"
              className="w-full bg-transparent px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-secondary-400"
            />
            <button type="submit" className="flex h-9 items-center gap-1 rounded-full bg-primary-500 px-5 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600">
              查询更新
            </button>
          </form>
          <div className="mx-auto mt-10 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { num: `${todayUpdates.length} 部`, label: "今日更新" },
              { num: `${ongoing.length} 部`, label: "连载中" },
              { num: `${completed.length} 部`, label: "已完结" },
              { num: `${categories.length} 类`, label: "内容分类" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-background-50/10 p-3 backdrop-blur-sm">
                <p className="font-display text-xl text-background-50">{s.num}</p>
                <p className="mt-1 text-xs text-background-100">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 今日更新 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeader
          id="today"
          title="今日更新"
          desc="实时整理今天上线的最新话，追更进度随时掌握"
          link={{ text: "查看全部", to: "/list" }}
        />
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {todayUpdates.map((u, i) => {
            const comic = getComicById(u.comicId);
            if (!comic) return null;
            return (
              <Link
                key={u.comicId}
                to={`/comic/${u.comicId}`}
                className={`flex items-center gap-3 px-4 py-3 transition-colors hover:bg-background-100 ${
                  i !== todayUpdates.length - 1 ? "border-b border-background-200/70" : ""
                }`}
              >
                <span className="w-6 text-center font-display text-sm text-primary-500">{String(i + 1).padStart(2, "0")}</span>
                <img src={comic.cover} alt={`${comic.title}封面`} className="h-12 w-9 rounded object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground-950">{comic.title}</p>
                  <p className="truncate text-xs text-foreground-600">
                    更新到第{u.chapterNum}话 · {u.chapterTitle}
                  </p>
                </div>
                <span className="hidden rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-800 sm:block">{comic.category}</span>
                <span className="whitespace-nowrap text-xs text-foreground-500">{u.time}</span>
                <i className="ri-arrow-right-s-line text-lg text-secondary-400"></i>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 热门榜单 */}
      <section className="bg-background-100/70">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <SectionHeader title="本周热榜" desc="人气与口碑兼备的追更热作" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {trending.map((c) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500 font-display text-lg text-background-50">
                  {c.weekRank}
                </span>
                <img src={c.cover} alt={`${c.title}封面`} className="h-16 w-12 rounded object-cover" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600">{c.title}</p>
                  <p className="mt-0.5 text-xs text-foreground-600">
                    {c.status === "连载中" ? `第${c.latestChapter}话` : `全${c.totalChapters}话`}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-accent-700">
                    <i className="ri-star-fill"></i>{c.rating}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 连载中 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeader
          title="连载中 · 持续追更"
          desc="每周都在更新的作品，最新话与更新时间实时同步"
          link={{ text: "更多连载", to: "/list?status=连载中" }}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
          {ongoing.slice(0, 8).map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* 已完结 */}
      <section className="bg-background-100/70">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <SectionHeader
            title="已完结 · 放心追到底"
            desc="全话完结的完整长篇，适合一口气从头追到尾"
            link={{ text: "更多完结", to: "/list?status=已完结" }}
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {completed.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* 分类 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeader title="按分类浏览" desc="不同题材，不同味道" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:gap-4">
          {categories.map((cat) => {
            const count = comics.filter((c) => c.category === cat).length;
            return (
              <Link
                key={cat}
                to={`/list?category=${encodeURIComponent(cat)}`}
                className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <div>
                  <p className="text-sm font-semibold text-foreground-950 group-hover:text-primary-700 md:text-base">{cat}</p>
                  <p className="mt-0.5 text-xs text-secondary-500">{count} 部作品</p>
                </div>
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-primary-500 group-hover:bg-primary-100">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FAQ 预览 */}
      <section className="bg-background-100/70">
        <div className="mx-auto w-full max-w-4xl px-4 py-12 md:px-6 md:py-16">
          <SectionHeader title="常见问题" desc="关于追更进度与连载状态，你可能想问的" link={{ text: "全部问题", to: "/faq" }} />
          <div className="space-y-3">
            {faqItems.slice(0, 4).map((item, idx) => (
              <div key={item.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
                >
                  <span className="text-sm font-medium text-foreground-900">{item.q}</span>
                  <i className={`ri-add-line text-lg text-primary-500 transition-transform ${openFaq === idx ? "rotate-45" : ""}`}></i>
                </button>
                {openFaq === idx && (
                  <p className="prose-line px-4 pb-4 text-sm text-foreground-600">{item.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary-600 to-accent-600 px-6 py-12 text-center md:px-12">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10">
            <h2 className="font-display text-2xl text-background-50 md:text-3xl">今天，你追的漫画更新了吗？</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-background-100 md:text-base">
              打开无尽漫画网，一眼看清今日更新与最新话进度，别再错过你追的每一部作品。
            </p>
            <Link
              to="/list"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 whitespace-nowrap hover:bg-background-100"
            >
              开始追更 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}